package fyi.moko.hotpl.ui.share

import androidx.compose.runtime.Composable
import kotlin.math.round

/**
 * Builds the plain-text message shared when a user shares a saved place (to Messages,
 * KakaoTalk, or any other target in the OS share sheet). Includes the place name, its
 * area label, and a **universal** Google Maps web link (tappable on any device/OS) when
 * coordinates are present. Notes and tags are intentionally excluded — they're often
 * private.
 *
 * Example:
 * ```
 * 📍 Park's BBQ
 * Vermont Ave, Koreatown
 * https://www.google.com/maps/search/?api=1&query=34.0625,-118.2919
 * ```
 */
fun buildSharePlaceText(
    name: String,
    locationContext: String?,
    latitude: Double?,
    longitude: Double?,
    approximate: Boolean = false,
): String = buildString {
    append("📍 ").append(name.trim())
    if (!locationContext.isNullOrBlank()) {
        append('\n').append(locationContext.trim())
    }
    if (latitude != null && longitude != null) {
        val lat = if (approximate) roundToApproxArea(latitude) else latitude
        val lng = if (approximate) roundToApproxArea(longitude) else longitude
        append('\n').append(buildShareMapsUrl(lat, lng))
    }
}

/**
 * Rounds a coordinate to 2 decimal places (~1.1 km) for a privacy-preserving
 * "approximate area" share — enough to point at the neighborhood without pinning
 * an exact home/address.
 */
private fun roundToApproxArea(coord: Double): Double = round(coord * 100.0) / 100.0

/**
 * A universal maps web link that opens at the given coordinates. Used inside shared text
 * so the recipient can tap it regardless of platform. Distinct from
 * `buildOpenInMapsUrl`, which builds a platform-local launch URI (`geo:` / `maps.apple`).
 */
private fun buildShareMapsUrl(latitude: Double, longitude: Double): String =
    "https://www.google.com/maps/search/?api=1&query=$latitude,$longitude"

/**
 * Returns a launcher that opens the platform share sheet with [text] as plain text,
 * surfacing Messages / KakaoTalk / other installed share targets.
 *
 * Android actual: `Intent.ACTION_SEND` (`text/plain`) via `Intent.createChooser`.
 * iOS actual:     `UIActivityViewController` presented over the current view controller.
 */
@Composable
expect fun rememberShareTextLauncher(): (text: String) -> Unit
